import { useEffect, useRef, useState } from 'react';

/**
 * Cross-property "network switcher" shown in the nav. The Uliunai sites are
 * separate SPAs on separate subdomains, so links are plain cross-origin <a>
 * tags (not router links). `currentId` highlights the active property.
 */
interface NetworkProperty {
  id: string;
  name: string;
  /** Live destination. Undefined ⇒ "coming soon" (not yet linkable). */
  url?: string;
  status: 'live' | 'soon';
}

const PROPERTIES: NetworkProperty[] = [
  { id: 'kf1', name: 'Killing Floor', url: 'https://kf1.uliunai.com', status: 'live' },
  { id: 'minecraft', name: 'Minecraft', url: 'https://mc.uliunai.com', status: 'live' },
  { id: 'kf2', name: 'Killing Floor 2', status: 'soon' },
  { id: 'cs16', name: 'Counter-Strike 1.6', status: 'soon' },
  { id: 'cs2', name: 'Counter-Strike 2', status: 'soon' },
  { id: 'l4d2', name: 'Left 4 Dead 2', status: 'soon' },
];

const HUB_URL = 'https://uliunai.com';

export default function GameSwitcher({ currentId }: { currentId: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Switch Uliunai game"
        className="flex items-center gap-1 rounded-lg px-1.5 py-1 hover:bg-white/5 transition-colors cursor-pointer"
      >
        <img src="/brand/ul-logo.png" alt="Uliunai" className="w-10 h-10 object-contain" />
        <i
          className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        ></i>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 rounded-xl border border-red-900/40 bg-black/95 backdrop-blur-md shadow-2xl shadow-red-950/50 p-3 z-50">
          <div className="px-1 pb-2 mb-2 border-b border-red-900/30 font-orbitron text-xs uppercase tracking-widest text-red-500">
            Uliunai Network
          </div>

          <div className="grid grid-cols-2 gap-1.5">
            {PROPERTIES.map((p) => {
              const isCurrent = p.id === currentId;
              const inner = (
                <>
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.status === 'live' ? 'bg-green-500 shadow-[0_0_6px] shadow-green-500/70' : 'bg-gray-600'}`}
                    ></span>
                    <span className="truncate">{p.name}</span>
                  </span>
                  {p.status === 'soon' && (
                    <span className="text-[10px] uppercase tracking-wide text-gray-600">Soon</span>
                  )}
                </>
              );
              const cls = `flex items-center justify-between gap-1 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                isCurrent
                  ? 'bg-red-950/50 text-white ring-1 ring-red-700/50'
                  : p.url
                    ? 'text-gray-300 hover:bg-red-950/40 hover:text-white cursor-pointer'
                    : 'text-gray-600 cursor-default'
              }`;

              if (p.url && !isCurrent) {
                return (
                  <a key={p.id} href={p.url} className={cls}>
                    {inner}
                  </a>
                );
              }
              return (
                <div key={p.id} className={cls} aria-current={isCurrent ? 'page' : undefined}>
                  {inner}
                </div>
              );
            })}
          </div>

          <a
            href={HUB_URL}
            className="mt-2 flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-semibold text-gray-200 hover:bg-red-950/40 hover:text-white transition-colors border-t border-red-900/30 pt-3"
          >
            <i className="ri-home-4-line"></i>
            Uliunai Hub
          </a>
        </div>
      )}
    </div>
  );
}
